// In-match DOM HUD: context-sensitive, collapsible panels for mobile landscape.
import type { GameState, PlayerId, Entity } from '../sim/types';
import { isBuilding, isUnit } from '../sim/types';
import { isAlive, isPowerShort, powerDeficit, buildingHasPower, radarActive, isHarvester } from '../sim/views';
import { getRally, garrisonedIds, garrisonReservedIds, isChanneling, hasMorph, getMorph } from '../sim/capabilities';
import type { Registry } from '../data/registry';
import type { ArtDef, BuildingDef } from '../data/defs';
import type { InputController } from '../input/controller';
import type { Minimap } from './minimap';
import { el } from './hud/dom';
import { Collapsible } from './hud/collapsible';
import { CommandMenuPanel } from './hud/command-menu-panel';
import { UnitOrdersPanel } from './hud/unit-orders-panel';
import { BuildingActionsPanel } from './hud/building-actions-panel';
import { SpellBar } from './hud/spell-bar';
import { SuperweaponStatus } from './hud/superweapon-status';
import { MatchSettingsScreen } from './hud/settings-screen';
import type { Settings } from '../storage/settings';
import { saveSettings } from '../storage/settings';
import type { AudioManager } from '../audio/audio';
import { placementConfirmHint, placementCostLabel } from '../input/placement';
import { radarOfflineHint } from './radar-hint';
import { CoachCard } from './coach/card';
import {
  advanceMatchCoach,
  initialMatchCoachStep,
  matchCoachCopy,
  RADAR_OFFLINE_HOW,
  RADAR_OFFLINE_TITLE,
  skipMatchCoachStep,
  type MatchCoachFacts,
  type MatchCoachStep,
} from './coach/progress';
import { isCompactUiSize } from './viewport';

export interface HudOptions {
  settings: Settings;
  audio: AudioManager;
  onSettingsChange: (settings: Settings) => void;
  soloPause?: {
    isPaused: () => boolean;
    onToggle: () => void;
  } | null;
  /** Developer sandbox has its own banner; skip the first-match coach. */
  sandbox?: boolean;
}

export class Hud {
  root = el('div', 'hud');
  private manaEl = el('span', 'stat-mana', '0');
  private powerEl = el('span', 'stat-power', '0');
  private powerStat = el('div', 'stat compact-stat power-stat');
  private selName = el('div', 'sel-name');
  private selDesc = el('div', 'sel-desc');
  private selMeta = el('div', 'sel-meta');
  private buildConfirm = el('div', 'build-confirm');
  private buildConfirmLabel = el('span', 'confirm-label');
  private buildConfirmBtn = el('button', 'btn confirm', 'Place');
  private buildCancelBtn = el('button', 'btn', 'Cancel');
  private result = el('div', 'result-overlay');
  private settingsScreen: MatchSettingsScreen;
  private debugEl = el('div', 'debug-overlay');
  private hintEl = el('div', 'hint-banner');
  private hintHideTimer = 0;
  private debugOn = false;
  private pauseOverlay = el('div', 'pause-overlay');
  private pauseBtn: HTMLButtonElement | null = null;

  private infoPanel: Collapsible;
  private minimapPanel: Collapsible;
  private minimapHint = el('p', 'minimap-hint');
  private commandMenu: CommandMenuPanel;
  private unitOrdersPanel: UnitOrdersPanel;
  private buildingActions: BuildingActionsPanel;
  private spellBar: SpellBar;
  private superweaponStatus: SuperweaponStatus;
  private panelContext = '';
  private hudTab: 'info' | 'command' = 'info';
  private lastSelectionKey = '';
  private panelTabRow = el('div', 'panel-tab-row');
  private infoTabBtn = el('button', 'btn panel-tab', 'Info');
  private commandTabBtn = el('button', 'btn panel-tab', 'Build');
  private cmdSidebar = el('div', 'cmd-sidebar');
  private cmdContent = el('div', 'cmd-content');
  private coach = new CoachCard();
  private coachStep: MatchCoachStep = 'done';
  private minimapCaption = el('p', 'minimap-caption');
  private readonly sandbox: boolean;
  private readonly hudOptions: HudOptions;

  onExit: (() => void) | null = null;

  setPlayerId(playerId: PlayerId): void {
    this.playerId = playerId;
    this.unitOrdersPanel.setPlayerId(playerId);
  }

  private syncSidebarVisibility(): void {
    const tabsVisible = this.panelTabRow.style.display !== 'none';
    const actionsVisible = this.buildingActions.row.style.display !== 'none';
    this.cmdSidebar.style.display = tabsVisible || actionsVisible ? 'flex' : 'none';
    this.cmdSidebar.parentElement?.classList.toggle('cmd-tabs-mode', tabsVisible);
  }

  private setHudTab(tab: 'info' | 'command'): void {
    this.hudTab = tab;
    this.infoTabBtn.classList.toggle('active', tab === 'info');
    this.commandTabBtn.classList.toggle('active', tab === 'command');
    const tabsMode = this.panelTabRow.style.display !== 'none';
    if (tabsMode) {
      this.infoPanel.root.style.display = tab === 'info' ? '' : 'none';
      this.commandMenu.panel.root.style.display = tab === 'command' ? '' : 'none';
      this.infoPanel.setOpen(true);
      this.commandMenu.panel.setOpen(true);
    } else {
      this.infoPanel.root.style.display = '';
      this.infoPanel.setOpen(tab === 'info');
      this.commandMenu.panel.setOpen(tab === 'command');
    }
  }

  constructor(
    private state: () => GameState,
    private registry: Registry,
    private controller: InputController,
    private playerId: PlayerId,
    minimap: Minimap,
    iconFor: (art: ArtDef, color: string) => HTMLCanvasElement,
    hudOptions: HudOptions,
  ) {
    this.hudOptions = hudOptions;
    this.sandbox = hudOptions.sandbox ?? false;
    const compact = isCompactUiSize(window.innerWidth, window.innerHeight);
    const top = el('div', 'topbar');
    const mana = el('div', 'stat compact-stat');
    mana.append(el('span', 'stat-label', 'Mana '), this.manaEl);
    this.powerStat.append(el('span', 'stat-label', 'Pwr '), this.powerEl);
    const dbgBtn = el('button', 'btn dbg-btn', '·');
    dbgBtn.title = 'Debug';
    dbgBtn.addEventListener('click', () => {
      this.debugOn = !this.debugOn;
      this.debugEl.style.display = this.debugOn ? 'block' : 'none';
    });
    const settingsBtn = el('button', 'btn menu-btn', 'Settings');
    settingsBtn.addEventListener('click', () => this.toggleSettings());
    if (hudOptions.soloPause) {
      this.pauseBtn = el('button', 'btn menu-btn', 'Pause');
      this.pauseBtn.addEventListener('click', () => hudOptions.soloPause!.onToggle());
    }
    this.spellBar = new SpellBar(registry, controller);
    this.superweaponStatus = new SuperweaponStatus(registry);
    top.append(
      mana,
      this.powerStat,
      this.spellBar.row,
      dbgBtn,
      ...(this.pauseBtn ? [this.pauseBtn] : []),
      settingsBtn,
    );

    const selBlock = el('div', 'sel-block');
    this.commandMenu = new CommandMenuPanel(registry, controller, iconFor, false, () => this.setHudTab('command'));
    this.buildingActions = new BuildingActionsPanel(state, registry, controller);
    selBlock.append(
      this.selName,
      this.selDesc,
      this.selMeta,
      this.commandMenu.trainQueueEl,
    );
    this.infoPanel = new Collapsible('Selection', !compact, () => this.setHudTab('info'));
    this.infoPanel.body.append(selBlock);

    this.infoTabBtn.addEventListener('click', () => this.setHudTab('info'));
    this.commandTabBtn.addEventListener('click', () => this.setHudTab('command'));
    this.commandTabBtn.title = 'Build & Train';
    this.panelTabRow.append(this.infoTabBtn, this.commandTabBtn);
    this.panelTabRow.style.display = 'none';

    this.unitOrdersPanel = new UnitOrdersPanel(state, registry, controller, playerId, false);

    this.cmdSidebar.append(this.panelTabRow, this.buildingActions.row);
    this.cmdContent.append(
      this.infoPanel.root,
      this.commandMenu.panel.root,
      this.unitOrdersPanel.panel.root,
    );
    this.cmdSidebar.style.display = 'none';

    const cmdCard = el('div', 'cmd-card hud-scroll');
    const cmdCardLayout = el('div', 'cmd-card-layout');
    cmdCardLayout.append(this.cmdSidebar, this.cmdContent);
    cmdCard.append(cmdCardLayout);
    const keepScroll = (e: Event) => e.stopPropagation();
    for (const node of [
      cmdCard,
      cmdCardLayout,
      this.cmdContent,
      this.cmdSidebar,
      this.spellBar.row,
      this.panelTabRow,
      this.commandMenu.panel.root,
      ...this.commandMenu.touchRoots,
      this.unitOrdersPanel.row,
      this.buildingActions.row,
    ]) {
      node.addEventListener('touchstart', keepScroll, { passive: true });
      node.addEventListener('touchmove', keepScroll, { passive: true });
    }

    const minimapWrap = el('div', 'minimap-wrap');
    this.minimapCaption.textContent = RADAR_OFFLINE_HOW;
    this.minimapCaption.style.display = 'none';
    minimapWrap.append(minimap.canvas, this.minimapHint, this.minimapCaption);
    this.minimapPanel = new Collapsible('Map', false);
    this.minimapPanel.body.append(minimapWrap);
    this.minimapPanel.root.classList.add('minimap-panel');

    this.buildConfirmBtn.addEventListener('click', () => this.controller.confirmPlacement());
    this.buildCancelBtn.addEventListener('click', () => this.controller.setMode('normal'));
    this.buildConfirm.append(this.buildConfirmLabel, this.buildConfirmBtn, this.buildCancelBtn);
    this.buildConfirm.style.display = 'none';

    this.settingsScreen = new MatchSettingsScreen({
      settings: hudOptions.settings,
      audio: hudOptions.audio,
      onSettingsChange: (settings) => {
        this.syncCoachEnabled();
        hudOptions.onSettingsChange(settings);
      },
      onLeaveMatch: () => this.onExit?.(),
      onReplayTips: () => {
        if (this.sandbox) return;
        this.coachStep = initialMatchCoachStep();
        this.syncCoach();
      },
    });

    this.coach.onSkip = () => this.finishCoach();
    this.coach.onGotIt = () => this.gotItCoach();
    this.syncCoachEnabled();

    const pauseCard = el('div', 'pause-card');
    pauseCard.append(
      el('h1', 'menu-title', 'Paused'),
      el('p', 'menu-sub', 'The match is frozen. Resume when you are ready.'),
    );
    const resumeBtn = el('button', 'btn big', 'Resume');
    resumeBtn.addEventListener('click', () => hudOptions.soloPause?.onToggle());
    pauseCard.append(resumeBtn);
    this.pauseOverlay.append(pauseCard);
    this.pauseOverlay.style.display = 'none';
    this.pauseOverlay.setAttribute('aria-hidden', 'true');

    this.root.append(
      top,
      this.superweaponStatus.root,
      this.minimapPanel.root,
      cmdCard,
      this.buildConfirm,
      this.spellBar.confirm,
      this.debugEl,
      this.hintEl,
      this.result,
      this.settingsScreen.root,
      this.pauseOverlay,
      this.coach.root,
    );
    this.result.style.display = 'none';
    this.debugEl.style.display = 'none';
    this.hintEl.style.display = 'none';
  }

  setPaused(paused: boolean): void {
    this.pauseOverlay.style.display = paused ? 'flex' : 'none';
    this.root.classList.toggle('paused', paused);
    this.pauseOverlay.setAttribute('aria-hidden', paused ? 'false' : 'true');
    if (this.pauseBtn) this.pauseBtn.textContent = paused ? 'Resume' : 'Pause';
  }

  showHint(text: string): void {
    if (this.hintHideTimer) {
      clearTimeout(this.hintHideTimer);
      this.hintHideTimer = 0;
    }
    if (!text) {
      this.hintEl.textContent = '';
      this.hintEl.style.display = 'none';
      return;
    }
    this.hintEl.textContent = text;
    this.hintEl.style.display = 'block';
    this.hintHideTimer = window.setTimeout(() => {
      this.hintEl.style.display = 'none';
      this.hintHideTimer = 0;
    }, 12_000);
  }

  setDebug(fps: number, tick: number, entities: number): void {
    if (!this.debugOn) return;
    const sel = this.controller.session.selection.size;
    this.debugEl.textContent = `${fps.toFixed(0)} fps · tick ${tick} · entities ${entities} · sel ${sel}`;
  }

  private toggleSettings(): void {
    if (this.settingsScreen.isOpen()) this.settingsScreen.close();
    else this.settingsScreen.open();
  }

  showResult(win: boolean): void {
    this.controller.clearSelection();
    this.result.innerHTML = '';
    const card = el('div', 'result-card');
    card.append(
      el('h1', 'result-title', win ? 'Victory' : 'Defeat'),
      el('p', 'result-hint', 'Drag or pinch to explore the map · two fingers to pan'),
      (() => {
        const b = el('button', 'btn big', 'Back to Menu');
        b.addEventListener('click', () => this.onExit?.());
        return b;
      })(),
    );
    this.result.append(card);
    this.result.style.display = 'flex';
  }

  private setPanelVisible(panel: Collapsible, visible: boolean): void {
    panel.root.style.display = visible ? '' : 'none';
  }

  private syncPanelLayout(
    inPlaceMode: boolean,
    showCommandMenu: boolean,
    showUnitPanel: boolean,
    ownBuilding: BuildingDef | null | undefined,
    selectionLabel: string,
    multiSelect: boolean,
  ): void {
    const key = [
      inPlaceMode ? 'place' : 'play',
      showCommandMenu ? 'cmd' : '',
      showUnitPanel ? 'units' : '',
      this.hudTab,
      selectionLabel,
    ].join('|');
    if (key === this.panelContext) return;
    this.panelContext = key;

    if (inPlaceMode) {
      this.infoPanel.setTitle(selectionLabel.startsWith('Deploy') ? 'Deploying' : selectionLabel.startsWith('Set') ? 'Rally' : 'Placing');
      this.panelTabRow.style.display = 'none';
      this.infoPanel.setOpen(true);
      this.commandMenu.panel.setOpen(false);
      this.unitOrdersPanel.panel.setOpen(false);
      return;
    }
    if (showUnitPanel) {
      this.panelTabRow.style.display = 'none';
      this.unitOrdersPanel.panel.setOpen(true);
      this.commandMenu.panel.setOpen(false);
      this.infoPanel.setTitle(selectionLabel);
      this.infoPanel.setOpen(!multiSelect);
      return;
    }
    if (showCommandMenu && ownBuilding) {
      this.panelTabRow.style.display = 'flex';
      this.infoPanel.setTitle(ownBuilding.name);
      this.unitOrdersPanel.panel.setOpen(false);
      this.setHudTab(this.hudTab);
      return;
    }
    this.panelTabRow.style.display = 'none';
    this.commandMenu.panel.setOpen(false);
    this.unitOrdersPanel.panel.setOpen(false);
    this.infoPanel.setTitle('Selection');
    this.infoPanel.setOpen(true);
  }

  update(): void {
    const st = this.state();
    this.superweaponStatus.update(st);
    const p = st.players.find((pl) => pl.id === this.playerId);
    if (!p) return;
    this.manaEl.textContent = Math.floor(p.mana).toString();
    const short = isPowerShort(st, this.playerId);
    const deficit = powerDeficit(st, this.playerId);
    this.powerEl.textContent = short ? `${p.powerUsed}/${p.power} (−${deficit})` : `${p.powerUsed}/${p.power}`;
    this.powerEl.classList.toggle('low', short);
    this.powerStat.classList.toggle('power-short', short);
    this.powerStat.title = short ? 'Low power — production and defenses offline. Build Ley Conduit (+60 pwr).' : '';

    const radarOn = radarActive(st, this.registry, this.playerId);
    const radarHint = st.ended ? null : radarOfflineHint(st, this.registry, this.playerId);
    if (st.ended) {
      this.minimapPanel.setTitle('Map');
      this.minimapPanel.root.classList.remove('minimap-offline');
      this.minimapPanel.root.title = '';
      this.minimapCaption.style.display = 'none';
    } else if (radarOn) {
      this.minimapPanel.setTitle('Map');
      this.minimapPanel.root.classList.remove('minimap-offline');
      this.minimapPanel.root.title = '';
      this.minimapCaption.style.display = 'none';
    } else {
      this.minimapPanel.setTitle(RADAR_OFFLINE_TITLE);
      this.minimapPanel.root.classList.add('minimap-offline');
      this.minimapPanel.root.title = RADAR_OFFLINE_HOW;
      this.minimapCaption.style.display = 'block';
    }
    this.minimapHint.textContent = radarHint ?? '';

    const session = this.controller.session;
    this.spellBar.update(p, session);

    const selIds = [...session.selection];
    const sel = selIds.map((id) => st.entities.get(id)).filter((e): e is Entity => !!e && isAlive(e));
    const single = sel.length === 1 ? sel[0]! : null;
    const singleBuilding = single && isBuilding(single) ? single : null;
    const unitsSelected = sel.some((e) => e.owner === this.playerId && e.kind === 'unit');
    const ownBuilding =
      singleBuilding?.owner === this.playerId ? this.registry.buildings.get(singleBuilding.defId) : null;

    const inBuildMode = session.mode === 'build';
    const inDeployMode = session.mode === 'deploy';
    const inRallyMode = session.mode === 'rally';
    const inGarrisonMode = session.mode === 'garrison';
    const inPlaceMode = inBuildMode || inDeployMode || inRallyMode || inGarrisonMode;
    const isHQ = !!ownBuilding?.isConstructionYard;
    const isProducer = !!ownBuilding?.producesUnits?.length && !ownBuilding.isConstructionYard;
    const showCommandMenu = !inPlaceMode && (isHQ || isProducer);
    const showUnitPanel = !inPlaceMode && unitsSelected;

    const selectionKey =
      single != null ? `b:${single.id}` : sel.length > 1 ? 'multi' : 'none';
    if (selectionKey !== this.lastSelectionKey) {
      this.lastSelectionKey = selectionKey;
      if (isHQ || isProducer) this.hudTab = 'command';
      else this.hudTab = 'info';
    }

    this.setPanelVisible(this.commandMenu.panel, showCommandMenu);
    this.setPanelVisible(this.unitOrdersPanel.panel, showUnitPanel);

    const commandContext = showCommandMenu
      ? isHQ
        ? { mode: 'hq' as const }
        : {
            mode: 'producer' as const,
            producerBuildingId: singleBuilding!.id,
            producerBuildingDefId: singleBuilding!.defId,
          }
      : null;
    if (showCommandMenu) {
      this.commandMenu.update(st, this.playerId, p, session, commandContext);
    }

    this.unitOrdersPanel.update(single, sel, inPlaceMode);
    this.buildingActions.update(p, ownBuilding, single, inPlaceMode, inRallyMode);

    if (inRallyMode) {
      this.selName.textContent = ownBuilding?.name ?? 'Set rally point';
      this.selDesc.textContent = singleBuilding && getRally(singleBuilding)
        ? 'Tap the map to move the rally point.'
        : 'Tap the map where new units should go after training.';
      this.selMeta.textContent = singleBuilding && getRally(singleBuilding) ? 'Rally point set — tap map to move' : 'No rally point';
      this.commandMenu.updateTrainQueue(this.registry, this.controller, null);
    } else if (inGarrisonMode) {
      this.selName.textContent = 'Garrison';
      this.selDesc.textContent = 'Tap an owned Arcane Bunker with free capacity.';
      this.selMeta.textContent = `${session.garrisonUnitIds.length} selected unit(s) ready to enter`;
      this.commandMenu.panel.setOpen(false);
      this.commandMenu.updateTrainQueue(this.registry, this.controller, null);
    } else if (inDeployMode && session.deployEntityId) {
      const campDef = this.registry.buildings.get('waystone_camp')!;
      this.selName.textContent = 'Deploy: Waystone Camp';
      this.selDesc.textContent = campDef.description;
      this.selMeta.textContent = 'Deploys in place if clear · click/tap map or Place';
      this.commandMenu.panel.setOpen(false);
      this.commandMenu.updateTrainQueue(this.registry, this.controller, null);
    } else if (inBuildMode && session.buildDefId) {
      const placing = this.registry.buildings.get(session.buildDefId)!;
      this.selName.textContent = `Placing: ${placing.name}`;
      this.selDesc.textContent = placing.description;
      this.selMeta.textContent = `${placing.cost} mana · ghost follows mouse · click map or Place`;
      this.commandMenu.panel.setOpen(false);
      this.commandMenu.updateTrainQueue(this.registry, this.controller, null);
    } else if (single) {
      const def = this.registry.units.get(single.defId) ?? this.registry.buildings.get(single.defId);
      this.selName.textContent = def?.name ?? single.defId;
      if (ownBuilding && singleBuilding) {
        this.selDesc.textContent = ownBuilding.description;
        const meta: string[] = [`${Math.ceil(single.hp)}/${single.maxHp} HP`];
        if (ownBuilding.powerUsed) meta.push(`uses ${ownBuilding.powerUsed} power`);
        if (ownBuilding.powerProduced) meta.push(`+${ownBuilding.powerProduced} power`);
        if (!buildingHasPower(st, this.registry, singleBuilding)) meta.unshift('⚡ OFFLINE — low power');
        else if (isPowerShort(st, this.playerId) && (ownBuilding.producesUnits || singleBuilding.buildProgress !== undefined)) {
          meta.unshift('⚡ SLOW — low power');
        }
        if (hasMorph(singleBuilding)) meta.push(`Packing ${Math.round((getMorph(singleBuilding)?.progress ?? 0) * 100)}%`);
        if (singleBuilding.repairing) meta.push('repairing');
        if (getRally(singleBuilding)) meta.push('rally set');
        if (ownBuilding.garrison) {
          const current = garrisonedIds(singleBuilding).length;
          const reserved = garrisonReservedIds(singleBuilding).length;
          meta.push(`garrison ${current + reserved}/${ownBuilding.garrison.capacity}`);
        }
        this.commandMenu.updateTrainQueue(
          this.registry,
          this.controller,
          isProducer ? singleBuilding : null,
        );
        this.selMeta.textContent = meta.join(' · ');
      } else if (def && 'role' in def && isUnit(single)) {
        this.selDesc.textContent = def.role;
        let meta = `${Math.ceil(single.hp)}/${single.maxHp} HP`;
        if (hasMorph(single)) {
          const morph = getMorph(single);
          meta += ` · ${morph?.action === 'deploy' ? 'Deploying' : 'Packing'} ${Math.round((morph?.progress ?? 0) * 100)}%`;
        }
        if (isChanneling(single)) meta += ' · conjuring mana';
        this.selMeta.textContent = meta;
        this.commandMenu.updateTrainQueue(this.registry, this.controller, null);
      } else {
        this.selDesc.textContent = '';
        this.selMeta.textContent = `${Math.ceil(single.hp)}/${single.maxHp} HP`;
        this.commandMenu.updateTrainQueue(this.registry, this.controller, null);
      }
    } else if (sel.length > 1) {
      this.selName.textContent = `${sel.length} units selected`;
      this.selDesc.textContent = 'Tap map to move. Use orders below.';
      this.selMeta.textContent = '';
      this.commandMenu.updateTrainQueue(this.registry, this.controller, null);
    } else {
      this.selName.textContent = 'Nothing selected';
      this.selDesc.textContent = 'Tap your HQ (purple) to build and train. Tap production buildings to train from there.';
      this.selMeta.textContent = short
        ? `Low power (−${deficit}) — build Ley Conduit or destroy structures`
        : 'Drag to select · two fingers, middle-mouse, or WASD to pan.';
      this.commandMenu.updateTrainQueue(this.registry, this.controller, null);
    }

    const selectionLabel =
      inRallyMode
        ? 'Set rally'
        : inDeployMode
        ? 'Deploy camp'
        : inBuildMode && session.buildDefId
        ? this.registry.buildings.get(session.buildDefId)!.name
        : single
          ? (this.registry.units.get(single.defId) ?? this.registry.buildings.get(single.defId))?.name ?? single.defId
          : sel.length > 1
            ? `${sel.length} units`
            : 'none';
    this.syncPanelLayout(
      inPlaceMode,
      showCommandMenu,
      showUnitPanel,
      ownBuilding,
      selectionLabel,
      sel.length > 1,
    );
    this.syncSidebarVisibility();
    this.syncCoach();

    if (inRallyMode) {
      this.buildConfirm.style.display = 'flex';
      this.buildConfirmLabel.textContent = singleBuilding && getRally(singleBuilding)
        ? 'Tap map to move rally point'
        : 'Tap map to set rally point';
      this.buildConfirmBtn.style.display = 'none';
    } else if (
      (session.mode === 'build' && session.buildDefId && (session.buildGhost || session.wallDragTiles?.length)) ||
      (session.mode === 'deploy' && session.buildGhost)
    ) {
      this.buildConfirmBtn.style.display = '';
      const def =
        session.mode === 'deploy'
          ? this.registry.buildings.get('waystone_camp')!
          : this.registry.buildings.get(session.buildDefId!)!;
      const wallPreview = session.wallDragTiles?.length;
      const ok = wallPreview ? this.controller.wallPlacementValid() : session.buildGhost!.valid;
      this.buildConfirm.style.display = 'flex';
      const hint = ok ? placementConfirmHint() : placementConfirmHint(session.buildGhost?.issue);
      const prefix = session.mode === 'deploy' ? 'Deploy' : def.name;
      const costLabel =
        session.mode === 'deploy'
          ? 'free'
          : placementCostLabel(def.cost, wallPreview ? session.wallDragTiles : null);
      this.buildConfirmLabel.textContent = `${prefix} (${costLabel}) ${hint}`;
      this.buildConfirmBtn.disabled = !ok;
    } else {
      this.buildConfirm.style.display = 'none';
      this.buildConfirmBtn.style.display = '';
    }

    if (st.ended && this.result.style.display === 'none') {
      this.showResult(p.team === st.winnerTeam);
    }
  }

  private syncCoachEnabled(): void {
    if (this.sandbox) {
      this.coachStep = 'done';
      this.coach.hide();
      return;
    }
    if (this.hudOptions.settings.showTips) {
      if (this.coachStep === 'done') this.coachStep = initialMatchCoachStep();
    } else {
      this.coachStep = 'done';
      this.coach.hide();
    }
  }

  private persistTips(showTips: boolean): void {
    this.hudOptions.settings.showTips = showTips;
    void saveSettings(this.hudOptions.settings);
    this.hudOptions.onSettingsChange(this.hudOptions.settings);
    this.syncCoachEnabled();
  }

  private finishCoach(): void {
    this.coachStep = 'done';
    this.coach.hide();
    if (this.hudOptions.settings.showTips) this.persistTips(false);
  }

  private gotItCoach(): void {
    if (this.coachStep === 'done') return;
    this.coachStep = skipMatchCoachStep(this.coachStep);
    if (this.coachStep === 'done') this.finishCoach();
    else this.syncCoach();
  }

  private collectCoachFacts(): MatchCoachFacts {
    const st = this.state();
    const session = this.controller.session;
    const selected = session.selection;
    let hqSelected = false;
    let ownedNonHqBuilding = false;
    let wispSelected = false;
    let wispHasOrder = false;
    for (const e of st.entities.values()) {
      if (e.owner !== this.playerId || !isAlive(e)) continue;
      if (isBuilding(e)) {
        const def = this.registry.buildings.get(e.defId);
        if (def?.isConstructionYard) {
          if (selected.has(e.id)) hqSelected = true;
        } else {
          ownedNonHqBuilding = true;
        }
      } else if (isUnit(e) && isHarvester(e)) {
        if (selected.has(e.id)) wispSelected = true;
        const busy =
          e.orders.length > 0 ||
          e.state === 'moving' ||
          e.state === 'harvesting' ||
          e.state === 'returning';
        if (busy) wispHasOrder = true;
      }
    }
    return {
      hqSelected,
      placingBuilding: session.mode === 'build' && !!session.buildDefId,
      ownedNonHqBuilding,
      wispSelected,
      wispHasOrder,
    };
  }

  private syncCoach(): void {
    if (this.sandbox || !this.hudOptions.settings.showTips || this.coachStep === 'done' || this.state().ended) {
      this.coach.hide();
      return;
    }
    const facts = this.collectCoachFacts();
    this.coachStep = advanceMatchCoach(this.coachStep, facts);
    if (this.coachStep === 'done') {
      this.finishCoach();
      return;
    }
    const copy = matchCoachCopy(this.coachStep, facts);
    if (copy) this.coach.show(copy);
    else this.coach.hide();
  }
}
